import { cn } from "@guru/shared/utils/cn";
import type React from "react";

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  /** Visual treatment of the section background. */
  tone?: "plain" | "tinted" | "ledger";
  /** Constrains the inner column. Defaults to the standard 7xl marketing column. */
  width?: "default" | "narrow";
  className?: string;
  children: React.ReactNode;
}

/**
 * Standard marketing section shell: consistent vertical rhythm, max-width
 * column, optional eyebrow/title/subtitle header and ledger-grid backdrop.
 */
export function Section({
  id,
  eyebrow,
  title,
  subtitle,
  tone = "plain",
  width = "default",
  className,
  children,
}: SectionProps) {
  return (
    <section
      id={id}
      className={cn("relative py-20 sm:py-24", tone === "tinted" && "bg-secondary/30", className)}
    >
      {tone === "ledger" && (
        <div
          className="absolute inset-0 ledger-grid ledger-grid-fade pointer-events-none"
          aria-hidden="true"
        />
      )}
      <div
        className={cn(
          "relative z-10 mx-auto w-full px-4 sm:px-6 lg:px-8",
          width === "narrow" ? "max-w-3xl" : "max-w-7xl"
        )}
      >
        {(eyebrow || title || subtitle) && (
          <div className="mb-12 text-center sm:mb-16">
            {eyebrow && (
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
                {eyebrow}
              </p>
            )}
            {title && (
              <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-lg text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
